import React, { useState } from "react";
import { Link } from "react-router-dom";
import StoreButtons from "./ui/StoreButtons";

const FOOTER_LOGO_SRC = "/RunCards_full_white_text_transparent.png";

/**
 * Footer — closes the marketing flow with one last download CTA, the
 * primary nav, and legal. Visual language matches the other dark sections
 * (HowItWorks, AppGallery): ambient radial gradient on black, uppercase
 * kicker, display headline, generous spacing, no neg-margin hacks.
 */
const Footer = () => {
  const [logoError, setLogoError] = useState(false);
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-white/[0.06] bg-black text-white">
      {/* Ambient gradient — same brand tones used in HowItWorks/AppGallery. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(120,127,154,0.16),transparent_55%),radial-gradient(circle_at_85%_80%,rgba(58,106,76,0.12),transparent_55%)]"
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 md:px-12">
        {/* Brand + contact columns. */}
        <div className="grid gap-12 pt-14 pb-12 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] md:gap-16 md:pt-16">
          {/* Brand mark + tagline */}
          <div>
            <Link to="/" aria-label="RunCards home" className="inline-block">
              {logoError ? (
                <span className="font-runcards-light text-2xl tracking-tight text-white">
                  RunCards
                </span>
              ) : (
                // Same wordmark PNG as the top-left menu logo. The source is a
                // 1024×1024 transparent canvas with the lockup in a thin center
                // band, so we render it as a background and crop to the logo via
                // background-size (zoom) + background-position (center the band).
                <div
                  role="img"
                  aria-label="RunCards"
                  className="h-7 w-40 sm:h-8 sm:w-48"
                  style={{
                    backgroundImage: `url('${FOOTER_LOGO_SRC}')`,
                    backgroundRepeat: "no-repeat",
                    // Zoom is a touch under the band height so the wordmark
                    // keeps a little vertical padding and never clips.
                    backgroundSize: "auto 980%",
                    backgroundPosition: "46% 45%",
                  }}
                />
              )}
            </Link>
            <p className="mt-4 max-w-xs font-runcards text-sm text-white/55">
              Connect with runners, discover run clubs, and find your next run.
            </p>
            <div className="mt-6">
              <StoreButtons variant="glass" className="flex-col sm:flex-row" />
            </div>
          </div>

          {/* Get in touch — replaces the old "Support" nav stub with a designed
              contact callout that matches the redesigned support page CTA. */}
          <div className="md:justify-self-end md:text-right md:max-w-xs">
            <p className="font-runcards-bold text-[11px] uppercase tracking-[0.28em] text-white/45">
              Get in touch
            </p>
            <a
              href="mailto:christopher@run.cards"
              className="mt-4 inline-block font-runcards-medium text-base sm:text-lg tracking-[-0.01em] text-white transition-colors hover:text-white/80"
            >
              christopher@run.cards
            </a>
            <p className="mt-2 font-runcards text-xs text-white/45">
              We typically reply within 24–48 hours.
            </p>
            <a
              href="/support.html"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-1.5 font-runcards text-sm text-white/65 transition-colors hover:text-white"
            >
              Visit the help center
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5"
              >
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>

        {/* Bottom bar. */}
        <div className="flex flex-col items-start justify-between gap-3 border-t border-white/[0.06] py-6 sm:flex-row sm:items-center">
          <p className="font-runcards text-xs text-white/45">
            © {currentYear} RunCards — Built for runners, by runners.
          </p>
          <div className="flex items-center gap-5">
            <a
              href="/privacy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="font-runcards text-xs text-white/45 transition-colors hover:text-white"
            >
              Privacy
            </a>
            <a
              href="/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              className="font-runcards text-xs text-white/45 transition-colors hover:text-white"
            >
              Terms
            </a>
            <a
              href="/support.html"
              target="_blank"
              rel="noopener noreferrer"
              className="font-runcards text-xs text-white/45 transition-colors hover:text-white"
            >
              Support
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
